
import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ConversationItem from "./conversation-item";
import type { Conversation } from "./types/conversation";

interface ConversationListProps {
  conversations: Conversation[];
  currentThreadId: string | null;
  isLoading: boolean;
  error: string | null;
  onSelectConversation: (threadId: string) => void;
  onDeleteClick: (conversation: Conversation, e: React.MouseEvent) => void;
  onRetry?: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  currentThreadId,
  isLoading,
  error,
  onSelectConversation,
  onDeleteClick,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <p className="text-sm text-muted-foreground">Laster samtaler...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>
          {error}
          {onRetry && (
            <button 
              onClick={onRetry}
              className="ml-2 text-xs underline hover:text-primary"
            >
              Prøv igjen
            </button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex justify-center items-center h-32 px-2">
        <p className="text-sm text-muted-foreground text-center">
          Ingen tidligere samtaler funnet
        </p>
      </div>
    );
  }

  // Sorter samtaler etter oppdateringsdato og fjerner duplikater basert på thread_id
  const uniqueConversations = conversations.reduce((acc, current) => {
    const x = acc.find(item => item.thread_id === current.thread_id);
    if (!x) {
      return acc.concat([current]);
    } else {
      // Hvis det finnes en nyere versjon, erstatt den gamle
      if (new Date(current.updated_at) > new Date(x.updated_at)) {
        return acc.map(item => item.thread_id === current.thread_id ? current : item);
      }
      return acc;
    }
  }, [] as Conversation[]);
  
  // Sorter etter nyeste først
  const sortedConversations = [...uniqueConversations].sort(
    (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  return (
    <ScrollArea className="flex-1">
      <div className="space-y-1 p-2">
        {sortedConversations.map((conversation) => (
          <ConversationItem
            key={`${conversation.id}`}
            conversation={conversation}
            isActive={conversation.thread_id === currentThreadId}
            onSelect={onSelectConversation}
            onDelete={onDeleteClick}
          />
        ))}
      </div>
    </ScrollArea>
  );
};

export default ConversationList;
